<!--
  Built on the shared dialog kit (.sd-scrim / .sd-modal / .sd-form in shared/styles/_dialog.scss),
  so coding a transaction looks like every other dialog in the app. Only the transaction
  reference and the AI note are local: the rest is the kit.
-->
<ng-container *ngIf="!inline; else inlineForm">
<div class="sd-scrim" (click)="onBackdropClick()">
  <div class="sd-modal sd-modal--form" role="dialog" aria-modal="true" aria-labelledby="mc-title"
    (click)="$event.stopPropagation()">
    <div class="sd-form">

      <div class="sd-form__head">
        <div class="sd-form__head-text">
          <h3 class="sd-form__title" id="mc-title">{{ title }}</h3>
          <p class="sd-form__subtitle">
            <ng-container *ngIf="isMulti">
              One set of details, applied to all {{ count }}.
            </ng-container>
            <ng-container *ngIf="!isMulti">
              The amount and date stay as the bank sent them.
            </ng-container>
          </p>
        </div>
        <button type="button" class="sd-icon-btn" aria-label="Close" (click)="onClose($event)">
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <ng-container *ngTemplateOutlet="formBody"></ng-container>

      <div class="sd-form__footer">
        <button type="button" class="sd-btn sd-btn--secondary" (click)="onClose($event)">Cancel</button>
        <button type="button" class="sd-btn sd-btn--primary" [disabled]="form.invalid || !transactions?.length"
          (click)="onSubmit()">
          {{ submitLabel }}
        </button>
      </div>
    </div>
  </div>
</div>
</ng-container>

<!-- Inline: the same form, inside a page or a panel that already shows the transaction and has
     its own close. No scrim, no header, no summary of what is being coded, and no Cancel. -->
<ng-template #inlineForm>
  <div class="sd-form mc-inline">
    <ng-container *ngTemplateOutlet="formBody"></ng-container>
    <div class="mc-inline__footer">
      <button type="button" class="sd-btn sd-btn--primary mc-inline__submit"
        [disabled]="busy || form.invalid || !transactions?.length" (click)="onSubmit()">
        {{ busy ? 'Coding…' : submitLabel }}
      </button>
    </div>
  </div>
</ng-template>

<ng-template #formBody>
  <form class="sd-form__body" [formGroup]="form" (ngSubmit)="onSubmit()" novalidate>

    <!-- What is being coded. Named rather than counted: coding the wrong transaction is
         the mistake worth catching, and it is only catchable if it is on screen. -->
    <div class="mc-refs" *ngIf="transactions?.length && !inline">
      <ng-container *ngIf="isSingle; else multiRef">
        <div class="mc-ref">
          <span class="mc-ref__date">{{ transactions[0].date | formatedDate }}</span>
          <span class="mc-ref__desc" [title]="transactions[0].description">
            {{ transactions[0].description }}
          </span>
          <span class="mc-ref__amount" [class.mc-ref__amount--negative]="transactions[0].amount < 0">
            {{ transactions[0].amount | currency:'':'symbol':'1.2-2' }}
          </span>
        </div>
        <div class="mc-refs__foot" *ngIf="transactions[0].accountName">
          {{ transactions[0].accountName }}
        </div>
      </ng-container>

      <ng-template #multiRef>
        <div class="mc-ref" *ngFor="let t of visibleTransactions">
          <span class="mc-ref__date">{{ t.date | formatedDate }}</span>
          <span class="mc-ref__desc" [title]="t.description">{{ t.description }}</span>
          <span class="mc-ref__amount" [class.mc-ref__amount--negative]="t.amount < 0">
            {{ t.amount | currency:'':'symbol':'1.2-2' }}
          </span>
        </div>
        <button type="button" class="mc-refs__more" *ngIf="hiddenCount > 0 || showAllRefs"
          (click)="toggleAllRefs()">
          {{ showAllRefs ? 'Show fewer' : '+ ' + hiddenCount + ' more' }}
        </button>
      </ng-template>
    </div>

    <div class="mc-ai-note" *ngIf="aiSuggestion && (supplierFromAi || accountFromAi || suggestedNewSupplier)">
      <span class="mc-ai-note__icon" aria-hidden="true">✨</span>
      <ng-container *ngIf="supplierFromAi && !supplierPossible">Supplier and account are pre-filled — check them before coding.</ng-container>
      <ng-container *ngIf="supplierFromAi && supplierPossible">The supplier is a guess, not named in the transaction — check it before coding.</ng-container>
      <ng-container *ngIf="!supplierFromAi && suggestedNewSupplier">None of your suppliers matched — create '{{ suggestedNewSupplier }}' or choose one.</ng-container>
      <ng-container *ngIf="!supplierFromAi && !suggestedNewSupplier">No supplier matched — choose one. The account is pre-filled.</ng-container>
    </div>

    <div class="sd-field">
      <label class="sd-field__label" for="ddlsupplier">
        Supplier
        <span class="mc-ai-tag" *ngIf="supplierFromAi && !supplierPossible" title="Suggested by AI">✨ Suggested</span>
        <span class="mc-ai-tag mc-ai-tag--check" *ngIf="supplierFromAi && supplierPossible"
              title="The transaction does not name this supplier: the AI's closest guess">Possible match · check</span>
      </label>
      <ng-select [ngModel]="selectedSupplierName || null" [items]="suppliers" formControlName="supplier"
        [searchable]="true" [clearable]="true" bindLabel="name" (change)="onSelectSupplier($event)"
        (keyup)="handleSupplierName($event.target.value)" placeholder="Select a supplier" [minlength]="0"
        class="sd-picker" appendTo="body" [ngModelOptions]="{standalone: true}" (blur)="handleSupplierBlur()"
        [closeOnSelect]="true" id="ddlsupplier">
        <ng-template ng-option-tmp let-item="item" let-index="index">
          <div class="tax-item" [innerHTML]="item.name"></div>
        </ng-template>
        <ng-template ng-footer-tmp *ngIf="isCreateSupplierItemShown || (isNewSupplier && isCreateSupplierItemShown)">
          <div class="create-new-supplier" (click)="createNewSupplier()">
            <span class="spinner-wrapper" *ngIf="creatingNewSupplier" style="margin-right: 5px;">
              <mat-spinner [diameter]="18"></mat-spinner>
            </span>
            <span *ngIf="!creatingNewSupplier">+ Create '{{newSupplier.name}}'</span>
            <span *ngIf="creatingNewSupplier" style="padding-top: 2px;">Adding '{{newSupplier.name}}'</span>
          </div>
        </ng-template>
      </ng-select>
      <!-- SD-1254: none of the books' suppliers is this one: create it in one click. -->
      <div class="mc-new-supplier" *ngIf="suggestedNewSupplier && !selectedSupplierId">
        <span class="mc-new-supplier__text">New supplier</span>
        <button type="button" class="mc-new-supplier__btn" (click)="createSuggestedSupplier()" [disabled]="creatingNewSupplier">
          {{ creatingNewSupplier ? 'Adding' : '+ Create' }} '{{ suggestedNewSupplier }}'
        </button>
      </div>
    </div>

    <div class="sd-field">
      <label class="sd-field__label" for="mc-account">
        Account
        <span class="mc-ai-tag" *ngIf="accountFromAi" title="Suggested by AI">✨ Suggested</span>
      </label>
      <ng-select id="mc-account" class="sd-picker" appendTo="body" [items]="accounts" bindLabel="name"
        formControlName="account" [searchable]="true" [clearable]="true" [loading]="loadingAccounts"
        loadingText="Loading accounts…" placeholder="Select an account" (change)="onSelectAccount($event)">
      </ng-select>
    </div>

    <!-- Two columns only when Class shares the row; alone, Tax is as wide as the rest. -->
    <div [class.sd-form__row]="isQuickBook && qboClasses?.length">
      <div class="sd-field">
        <label class="sd-field__label" for="mc-tax">Tax</label>
        <ng-select id="mc-tax" class="sd-picker" appendTo="body" [items]="taxRates" bindLabel="name"
          bindValue="id" formControlName="taxRate" [searchable]="false" [clearable]="true"
          [loading]="loadingTaxRates" loadingText="Loading tax rates…" placeholder="Select a tax rate"
          (change)="onSelectTaxRate($event)">
        </ng-select>
      </div>

      <div class="sd-field" *ngIf="isQuickBook && qboClasses?.length">
        <label class="sd-field__label" for="mc-class">
          Class <span class="sd-field__optional">Optional</span>
        </label>
        <ng-select id="mc-class" class="sd-picker" appendTo="body" [items]="qboClasses" bindLabel="name"
          bindValue="id" formControlName="qboClass" [searchable]="true" [clearable]="true"
          placeholder="Select a class" (change)="onSelectQBOClass($event)">
        </ng-select>
      </div>
    </div>

    <div class="sd-field">
      <label class="sd-field__label" for="mc-details">
        Details <span class="sd-field__optional">Optional</span>
      </label>
      <textarea id="mc-details" class="sd-input mc-details" formControlName="details" rows="3"
        placeholder="What was this for?"></textarea>
    </div>

    <!-- Lets Enter submit from any field. -->
    <button type="submit" hidden></button>
  </form>
</ng-template>
